import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import Button from 'Components/Atoms/Button/Button';
import { uploadInvoiceToERP } from 'Services/integrations';
import useContact from 'Hooks/useContact';
import useIntegrations from 'Hooks/useIntegrations';
import { trackError } from 'Utils/errorMonitoring';
import { useErrors } from 'Components/Organisms/ErrorsProvider';

export default function InvoiceUploadERPDrawer(props) {
    const { invoice, onClose } = props;
    const { t } = useTranslation('invoices');
    const queryClient = useQueryClient();
    const [isUploading, setIsUploading] = useState(false);
    const { showUnknownError } = useErrors();

    const { contact } = useContact(invoice?.invoice_party_id);
    const { integrations } = useIntegrations();

    const integration = integrations.find((i) => i.id === contact?.integration_id);

    const handleUpload = async () => {
        if (!contact?.integration_id || !invoice?.id) return;

        setIsUploading(true);
        try {
            await uploadInvoiceToERP(contact.integration_id, invoice.id);
            await queryClient.invalidateQueries({ queryKey: ['invoices'] });
            onClose();
        } catch (error) {
            trackError(error);
            showUnknownError();
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <div className="flex flex-col h-full">
            <div className="grow">
                <p className="text-gray-500 mb-6">{t('upload-to-erp-description')}</p>
                {integration && (
                    <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg mb-6">
                        {integration.icon && (
                            <img src={integration.icon} alt={integration.name} className="w-10 h-10 rounded" />
                        )}
                        <div>
                            <div className="font-semibold">{integration.name}</div>
                            <div className="text-sm text-gray-500">{integration.service}</div>
                        </div>
                    </div>
                )}
                <Button
                    className="w-full"
                    variant="primary"
                    onClick={handleUpload}
                    isBusy={isUploading}
                    disabled={!contact?.integration_id}
                >
                    {t('upload-to-erp')}
                </Button>
            </div>
        </div>
    );
}

InvoiceUploadERPDrawer.propTypes = {
    invoice: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
};
